import { UpgradeScenario, UpgradeScenarioConfig, UpgradeFeature, YearlyPriceBreakdown } from "./PackageUpgradeModal.type";

// Shared feature definitions
const FEATURE_ADVANCED_EDITING: UpgradeFeature = { label: "Advanced Editing Tools", description: "Fine-tune your AI-generated designs with precision editing capabilities." };
const FEATURE_RUNWAY: UpgradeFeature = { label: "Runway Image Viewing", description: "See your designs come to life with full access to runway-style imagery." };
const FEATURE_TECH_PACK: UpgradeFeature = { label: "Tech Pack Generation", description: "Automatically generate professional tech packs ready for production." };
const FEATURE_HIRES: UpgradeFeature = { label: "High-Resolution Image Generation", description: "Export and generate high-resolution images — print and production ready." };
const FEATURE_TREND_TRAINING: UpgradeFeature = { label: "Trend Training", description: "Train your AI on the latest fashion trends to keep your designs ahead of the curve." };
const FEATURE_BRAND_DNA: UpgradeFeature = { label: "Brand DNA Training", description: "Teach the AI your unique brand identity so every generation feels unmistakably yours." };
const FEATURE_AI_PHOTOSHOOT: UpgradeFeature = { label: "AI Photoshooting", description: "Generate stunning, studio-quality campaign and product photos powered by AI." };

const designStarterFeatures: UpgradeFeature[] = [FEATURE_ADVANCED_EDITING, FEATURE_RUNWAY, FEATURE_TECH_PACK, FEATURE_HIRES];
const designStarterFeaturesYearly: UpgradeFeature[] = [FEATURE_ADVANCED_EDITING, FEATURE_RUNWAY, FEATURE_HIRES];
const designProFeatures: UpgradeFeature[] = [FEATURE_ADVANCED_EDITING, FEATURE_RUNWAY, FEATURE_TECH_PACK, FEATURE_HIRES, FEATURE_TREND_TRAINING, FEATURE_BRAND_DNA, FEATURE_AI_PHOTOSHOOT];

const photoshootStarterFeatures: UpgradeFeature[] = [
  { label: "High-Quality AI Photoshooting", description: "Generate stunning, studio-quality product and campaign photos powered by AI." },
  { label: "Try-On Feature", description: "Instantly visualize garments on models with AI-powered virtual try-on." },
  { label: "Video Feature", description: "Bring your designs to life with AI-generated video content." },
  { label: "Editing Tools", description: "Fine-tune your visuals with precision editing capabilities." },
];

// Shared yearly breakdowns
const designStarterYearlyBreakdown: YearlyPriceBreakdown = {
  fullPrice: "$540/yr",
  discountAmount: "$81",
  discountPercentage: 15,
  finalPrice: "$459/yr",
  monthlyEquivalent: "~$38/mo",
  savingsNote: "You save $81 compared to the monthly plan by staying yearly!",
};

export const UPGRADE_SCENARIOS: UpgradeScenarioConfig[] = [
  {
    id: UpgradeScenario.MonthlyStarterToDesignStarter,
    oldPlan: { name: "Starter", price: "$15/mo", billingCycle: "monthly" },
    newPlan: { name: "Design Starter", price: "$45/mo", billingCycle: "monthly" },
    features: designStarterFeatures,
  },
  {
    id: UpgradeScenario.MonthlyEssentialToDesignStarter,
    oldPlan: { name: "Essential", price: "$35/mo", billingCycle: "monthly" },
    newPlan: { name: "Design Starter", price: "$45/mo", billingCycle: "monthly" },
    features: designStarterFeatures,
  },
  {
    id: UpgradeScenario.MonthlyPremiumToDesignPro,
    oldPlan: { name: "Premium", price: "$80/mo", billingCycle: "monthly" },
    newPlan: { name: "Design Pro", price: "$160/mo", billingCycle: "monthly" },
    features: designProFeatures,
  },
  {
    id: UpgradeScenario.YearlyPremiumToDesignPro,
    oldPlan: { name: "Premium (Yearly)", price: "$768/mo", billingCycle: "yearly" },
    newPlan: { name: "Design Pro (Yearly)", price: "$1,632/mo", billingCycle: "yearly" },
    features: designProFeatures,
    yearlyBreakdown: {
      fullPrice: "$1,920/mo",
      discountAmount: "$288",
      discountPercentage: 15,
      finalPrice: "$1,632/mo",
      monthlyEquivalent: "~$136/mo",
      savingsNote: "You save $288 compared to the monthly plan by staying yearly!",
    },
  },
  {
    id: UpgradeScenario.YearlyEssentialToDesignStarter,
    oldPlan: { name: "Essential (Yearly)", price: "$336/mo", billingCycle: "yearly" },
    newPlan: { name: "Design Starter (Yearly)", price: "$459/mo", billingCycle: "yearly" },
    features: designStarterFeaturesYearly,
    yearlyBreakdown: designStarterYearlyBreakdown,
  },
  {
    id: UpgradeScenario.YearlyStarterToDesignStarter,
    oldPlan: { name: "Starter (Yearly)", price: "$144/mo", billingCycle: "yearly" },
    newPlan: { name: "Design Starter (Yearly)", price: "$459/mo", billingCycle: "yearly" },
    features: designStarterFeaturesYearly,
    yearlyBreakdown: designStarterYearlyBreakdown,
  },
  {
    id: UpgradeScenario.MonthlyFoundationalToPhotoshootStarter,
    oldPlan: { name: "Foundational", price: "$39/mo", billingCycle: "monthly" },
    newPlan: { name: "Photoshoot Starter", price: "$45/mo", billingCycle: "monthly" },
    features: photoshootStarterFeatures,
  },
  {
    id: UpgradeScenario.YearlyFoundationalToPhotoshootStarter,
    oldPlan: { name: "Foundational (Yearly)", price: "$374.40/mo", billingCycle: "yearly" },
    newPlan: { name: "Photoshoot Starter (Yearly)", price: "$459/mo", billingCycle: "yearly" },
    features: photoshootStarterFeatures,
    yearlyBreakdown: designStarterYearlyBreakdown,
  },
  {
    id: UpgradeScenario.YearlyPrestigeToCreativeSuite,
    oldPlan: { name: "Prestige (Yearly)", price: "$559.40/mo", billingCycle: "yearly" },
    newPlan: { name: "Creative Suite (Yearly)", price: "$550/mo", billingCycle: "yearly" },
    features: photoshootStarterFeatures,
    yearlyBreakdown: designStarterYearlyBreakdown,
  },
];

export const UPGRADE_SCENARIO_MAP = Object.fromEntries(UPGRADE_SCENARIOS.map((s) => [s.id, s])) as Record<UpgradeScenario, UpgradeScenarioConfig>;
